import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';

import { accountSelector } from '../../account/account-selectors';
import { loadAccount } from '../../account/actions/load-account';
import { loadJobRoles } from '../../action/load-job-roles';
import { loadOrgJobs } from '../../job/actions/load-org-jobs';
import { userSelector } from '../../reducers/current-user-selectors';
import { jobRolesSelector } from '../../reducers/job-role-selectors';
import { jobsForOrgIdSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { EmployeeUserSelector } from '../../widgets/employee-user-selector';
import { LanguageSelector } from '../../widgets/language-selector';
import { loadOrg } from '../actions/load-org';

import { UiButton } from '../../ui/ui-button';

import { formatLocation } from '../../util/formatters';
import {
  groupJobsByLocation,
  SortedJobsType,
  sortJobLocations,
} from '../../util/group-jobs-by-location';
import { JOB_ROLES } from '../../util/job-roles';
import { HIRING_PERMISSION, EMPLOYEE_PERMISSION } from '../../util/permissions';
import { RequiredField } from '../../widgets/required-field';
import { StateAutocomplete } from '../../widgets/state-autocomplete';

const mapStateToProps = (state: any, { orgId }: InputProps) => {
  const currentUser = userSelector(state);
  const org = orgSelector(state, orgId);

  return {
    currentUser,
    org,
    jobRoles: jobRolesSelector(state),
    jobs: jobsForOrgIdSelector(state, orgId),
    account: org ? accountSelector(state, org.accountId) : null,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadAccount: (accountId: number) => dispatch(loadAccount(accountId)),
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadJobRoles: () => dispatch(loadJobRoles()),
    loadOrgJobs: () => dispatch(loadOrgJobs(orgId)),
  },
});

interface InputProps extends JobFlowStep {
  orgId: number;
  job: Partial<Job>;
}

type State = {
  job: Partial<Job>;
  locationFormVisible: boolean;
  hasErrors: boolean;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

class JobFlowDetailsComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { job, jobs } = this.props;
    this.state = {
      job,
      locationFormVisible: Boolean(this.getSortedAvailableLocations(jobs).length),
      hasErrors: false,
    };
  }

  componentDidMount() {
    this.props.actions.loadOrg();
    this.props.actions.loadOrgJobs();
    this.props.actions.loadJobRoles();

    if (this.props.org) {
      this.props.actions.loadAccount(this.props.org.accountId);
    }
  }

  componentDidUpdate() {
    this.props.actions.loadOrg();
    this.props.actions.loadOrgJobs();
    this.props.actions.loadJobRoles();

    if (this.props.org) {
      this.props.actions.loadAccount(this.props.org.accountId);
    }
  }

  getSortedAvailableLocations = (jobs: Array<Job> | null): SortedJobsType => {
    const { org, account } = this.props;
    const sortedJobLocations = jobs ? groupJobsByLocation(jobs) : {};

    if (org && account) {
      // AEs often create a job immediately after setting up or editing an account while on demos
      // In this case we'd want to grab the account from state to get the most updated information
      const orgLocationDisplay = formatLocation({ city: org.city, state: org.state });
      const orgLocation = orgLocationDisplay?.toUpperCase();
      const accountLocationDisplay = formatLocation({
        city: account.businessCity,
        state: account.businessState,
      });
      const accountLocation = accountLocationDisplay?.toUpperCase();

      // Add the org address and account address to the job locations list, default to data from jobs at that location if present
      if (accountLocation && accountLocationDisplay) {
        const currentLocationDetails = sortedJobLocations[accountLocation]?.location || {};
        // demo jobs are created with only the city/state, making it look like the account address doesn't fill properly
        // if there is a job at the account location that has a street address use the full address for that job, if not use the account's address (instead of coalescing each field)
        const hasExistingAddressDetails = !!currentLocationDetails.officeStreet;

        sortedJobLocations[accountLocation] = {
          jobs: sortedJobLocations[accountLocation]?.jobs ?? [],
          location: {
            city: currentLocationDetails.city ?? account.businessCity,
            state: currentLocationDetails.state ?? account.businessState,
            officeCity: hasExistingAddressDetails
              ? currentLocationDetails.officeCity
              : account.businessCity,
            officeState: hasExistingAddressDetails
              ? currentLocationDetails.officeState
              : account.businessState,
            officeStreet: hasExistingAddressDetails
              ? currentLocationDetails.officeStreet
              : account.businessStreet,
            officeUnit: hasExistingAddressDetails
              ? currentLocationDetails.officeUnit
              : account.businessUnit,
            officeZipCode: hasExistingAddressDetails
              ? currentLocationDetails.officeZipCode
              : account.businessZipCode,
          },
          displayName: sortedJobLocations[accountLocation]?.displayName ?? accountLocationDisplay,
        };
      }
      if (orgLocation && orgLocationDisplay) {
        const currentLocationDetails = sortedJobLocations[orgLocation]?.location || {
          officeCity: '',
          officeState: null,
          officeStreet: '',
          officeUnit: '',
          officeZipCode: '',
        };

        sortedJobLocations[orgLocation] = {
          jobs: sortedJobLocations[orgLocation]?.jobs ?? [],
          location: {
            ...currentLocationDetails,
            city: currentLocationDetails.city ?? org.city,
            state: currentLocationDetails.state ?? org.state,
          },
          displayName: sortedJobLocations[orgLocation]?.displayName ?? orgLocationDisplay,
        };
      }
    }

    return sortJobLocations(sortedJobLocations);
  };

  toggleAddLocation = () => {
    this.setState((prevState) => ({
      job: {
        ...prevState.job,
        city: '',
        state: null,
        officeCity: '',
        officeState: null,
        officeStreet: '',
        officeUnit: '',
        officeZipCode: '',
      },
      locationFormVisible: !prevState.locationFormVisible,
    }));
  };

  handleFormChange =
    (key: keyof Job) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    ) => {
      const { value } = e.target;
      this.setState(
        (prevState) => {
          const job = { ...prevState.job };
          // Necessary because jobRoleId is a number and not a string (otherwise this would just be inferred to be string)
          job[key] = value as any;
          return { job };
        },
        () => this.props.onDirty(true),
      );
    };

  handleStateChange =
    (field: 'state' | 'officeState' | 'hiringManagerUsername') => (newValue: string | null) => {
      this.setState(
        (prevState) => {
          const job = { ...prevState.job };
          job[field] = newValue;
          return { job };
        },
        () => this.props.onDirty(true),
      );
    };

  handleLanguageChange = (newValue: string | null) => {
    this.setState(
      (prevState) => {
        const job = { ...prevState.job };
        job.language = newValue || undefined;
        return { job };
      },
      () => this.props.onDirty(true),
    );
  };

  handleCurrentUserCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.handleStateChange('hiringManagerUsername')(
      event.target.checked ? this.props.currentUser.username : null,
    );
  };

  handleLocationSelect = (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    // This isn't _really_ possible - the values of the target are the
    // keys of the sortedJobLocations. Just being defensive.
    if (!this.props.jobs) {
      return;
    }
    const sortedJobLocations = this.getSortedAvailableLocations(this.props.jobs);
    const { value } = e.target;

    const jobLocation = sortedJobLocations[value as string];

    this.setState(
      (prevState: State) => {
        const jobUpdate = {
          ...prevState.job,
          city: jobLocation.location.city,
          state: jobLocation.location.state,
          officeCity: jobLocation.location.officeCity || '',
          officeState: jobLocation.location.officeState || '',
          officeStreet: jobLocation.location.officeStreet || '',
          officeUnit: jobLocation.location.officeUnit || '',
          officeZipCode: jobLocation.location.officeZipCode || '',
        };

        jobLocation.jobs.forEach((jobWithSameLocation: Job) => {
          if (!jobUpdate.officeCity && jobWithSameLocation.officeCity) {
            jobUpdate.officeCity = jobWithSameLocation.officeCity;
          }
          if (!jobUpdate.officeState && jobWithSameLocation.officeState) {
            jobUpdate.officeState = jobWithSameLocation.officeState;
          }
          if (!jobUpdate.officeStreet && jobWithSameLocation.officeStreet) {
            jobUpdate.officeStreet = jobWithSameLocation.officeStreet;
          }
          if (!jobUpdate.officeUnit && jobWithSameLocation.officeUnit) {
            jobUpdate.officeUnit = jobWithSameLocation.officeUnit;
          }
          if (!jobUpdate.officeZipCode && jobWithSameLocation.officeZipCode) {
            jobUpdate.officeZipCode = jobWithSameLocation.officeZipCode;
          }
        });
        return { job: jobUpdate };
      },
      () => this.props.onDirty(true),
    );
  };

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { job } = this.state;

    const requiredFields: Array<keyof Job> = [
      'title',
      'jobRoleId',
      'employmentType',
      'city',
      'state',
    ];

    if (!requiredFields.every((key: keyof Job) => job[key])) {
      this.setState({ hasErrors: true });
    } else {
      this.props.onNext(job);
    }
  };

  render() {
    const { saving, jobs, jobRoles, editing, orgId, currentUser } = this.props;
    const { job, locationFormVisible, hasErrors } = this.state;

    if (!jobRoles || !jobs) {
      return null;
    }

    const canChangeHiringManager =
      currentUser.employeesPermission === EMPLOYEE_PERMISSION.ALL &&
      currentUser.hiringPermission === HIRING_PERMISSION.ALL;
    const { HiringManager } = job;
    const sortedJobRoles = jobRoles.sort((a, b) => {
      // Float the "Other" option to the bottom of the list
      if (a.jobRoleId === JOB_ROLES.OTHER) {
        return 1;
      }
      if (b.jobRoleId === JOB_ROLES.OTHER) {
        return -1;
      }

      return a.name.localeCompare(b.name);
    });

    const jobLocation =
      (job.city && job.state && `${job.city?.toUpperCase()}, ${job.state?.toUpperCase()}`) || '';
    const sortedJobLocations = this.getSortedAvailableLocations(jobs);
    const loc = sortedJobLocations[jobLocation] ? jobLocation : '';

    return (
      <form noValidate onSubmit={this.handleSubmit}>
        <div className="card card-body u-margin-top-xl">
          <div className="typo-h6">Details</div>
          <RequiredField
            className="u-margin-top"
            label="Job Title"
            value={job.title}
            checkForErrors={hasErrors}
          >
            <Input onChange={this.handleFormChange('title')} />
          </RequiredField>
          <RequiredField
            className="u-margin-top"
            label="Role "
            value={job.jobRoleId || ''}
            checkForErrors={hasErrors}
          >
            <Select onChange={this.handleFormChange('jobRoleId')}>
              {sortedJobRoles.map((role) => (
                <MenuItem key={role.jobRoleId} value={role.jobRoleId}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </RequiredField>
          <RequiredField
            value={job.employmentType}
            className="u-margin-top"
            checkForErrors={hasErrors}
          >
            <RadioGroup row onChange={this.handleFormChange('employmentType')}>
              <FormControlLabel
                value="FULL_TIME"
                control={<Radio color="secondary" />}
                label="Full-time"
              />
              <FormControlLabel
                value="PART_TIME"
                control={<Radio color="secondary" />}
                label="Part-time"
              />
              <FormControlLabel
                value="FULL_TIME_OR_PART_TIME"
                control={<Radio color="secondary" />}
                label="Full-time or Part-time"
              />
            </RadioGroup>
          </RequiredField>
          <RequiredField value={job.language || 'en'} checkForErrors={hasErrors}>
            <LanguageSelector
              label="Application Language *"
              size="small"
              value={job.language || 'en'}
              onChange={this.handleLanguageChange}
            />
          </RequiredField>
          {job.language === 'es' ? (
            <div className="typo-subtitle">The application questions will appear in Spanish</div>
          ) : null}
          {job.language && job.language !== 'es' && job.language !== 'en' ? (
            <div className="typo-subtitle">
              The application questions will still appear in English, but by setting the language
              you have helped us to know which language our customers would like to use.
            </div>
          ) : null}
        </div>
        <div className="card card-body u-margin-top-xl">
          <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexStart">
            <div>
              <div className="typo-h6">Hiring Manager</div>
              <div className="typo-subtitle">
                The person who is primarily responsible for this job. They will be signed up for all
                notifications automatically.
              </div>
            </div>
          </div>
          {canChangeHiringManager ? (
            <EmployeeUserSelector
              label="Hiring Manager"
              orgId={orgId}
              value={job.hiringManagerUsername || null}
              onChange={this.handleStateChange('hiringManagerUsername')}
            />
          ) : !job.hiringManagerUsername || job.hiringManagerUsername === currentUser.username ? (
            <div className="u-margin-top-sm u-flex-center">
              <Checkbox
                className="u-no-padding"
                checked={job.hiringManagerUsername === currentUser.username}
                onChange={this.handleCurrentUserCheckboxChange}
              />
              <div className="typo-title u-margin-left-md">
                I am the hiring manager for this job
              </div>
            </div>
          ) : (
            <div className="u-margin-top-sm">
              {HiringManager
                ? `${HiringManager.firstName} ${HiringManager.lastName} `
                : 'Someone else '}
              is the hiring manager. If you want to change this, please contact your account
              administrator.
            </div>
          )}
        </div>
        <div className="card card-body u-margin-top-xl">
          <div className="u-display-flex u-justifyContent-spaceBetween u-alignItems-flexStart">
            <div>
              <div className="typo-h6">Job Location</div>
              <div className="typo-subtitle">The location where the job takes place</div>
            </div>
            {jobs.length > 0 && locationFormVisible ? (
              <UiButton
                className="u-margin-left"
                onClick={this.toggleAddLocation}
                variant="dismiss"
              >
                Cancel
              </UiButton>
            ) : null}
          </div>
          {!locationFormVisible ? (
            <div className="u-display-flex u-alignItems-flexEnd">
              <RequiredField label="Job Location " value={loc} checkForErrors={hasErrors}>
                <Select onChange={this.handleLocationSelect}>
                  {Object.keys(sortedJobLocations).map((location) => (
                    <MenuItem key={location} value={location}>
                      {sortedJobLocations[location].displayName}
                    </MenuItem>
                  ))}
                </Select>
              </RequiredField>
              <UiButton
                className="u-flexShrink-0 u-margin-left"
                onClick={this.toggleAddLocation}
                variant="tertiary"
                size="xs"
              >
                + Add Location
              </UiButton>
            </div>
          ) : (
            <div className="u-flex-center">
              <RequiredField
                label="City"
                value={job.city}
                checkForErrors={hasErrors}
                className="u-margin-right"
              >
                <Input onChange={this.handleFormChange('city')} />
              </RequiredField>
              <RequiredField value={job.state} checkForErrors={hasErrors} className="u-margin-left">
                <StateAutocomplete
                  onChange={this.handleStateChange('state')}
                  value={job.state || null}
                />
              </RequiredField>
            </div>
          )}
          {loc || locationFormVisible ? (
            <>
              <div className="typo-h6 u-margin-top-lg">Office Location</div>
              <div className="typo-subtitle">
                Location where the applicant should go to interview
              </div>
              <div>
                <div className="u-display-flex">
                  <FormControl fullWidth className="u-margin-right">
                    <InputLabel margin="dense">Street</InputLabel>
                    <Input
                      value={job.officeStreet}
                      onChange={this.handleFormChange('officeStreet')}
                    />
                  </FormControl>
                  <FormControl fullWidth className="u-margin-left">
                    <InputLabel margin="dense">Unit</InputLabel>
                    <Input value={job.officeUnit} onChange={this.handleFormChange('officeUnit')} />
                  </FormControl>
                </div>
                <div className="u-display-flex">
                  <FormControl fullWidth className="u-margin-right">
                    <InputLabel margin="dense">City</InputLabel>
                    <Input value={job.officeCity} onChange={this.handleFormChange('officeCity')} />
                  </FormControl>
                  <StateAutocomplete
                    onChange={this.handleStateChange('officeState')}
                    value={job.officeState || null}
                  />
                  <FormControl fullWidth className="u-margin-left">
                    <InputLabel margin="dense">Zip Code</InputLabel>
                    <Input
                      value={job.officeZipCode}
                      onChange={this.handleFormChange('officeZipCode')}
                    />
                  </FormControl>
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="u-textAlign-right u-margin-top">
          <UiButton type="submit" loading={saving}>
            {editing ? 'Save' : 'Next'}
          </UiButton>
        </div>
      </form>
    );
  }
}

export const JobFlowDetails = connect(mapStateToProps, mapDispatchToProps)(JobFlowDetailsComponent);
